import { LitElement, css, html, nothing } from '@lit'
import { t } from '@nova/utils'

class NovaSearchResult extends LitElement {
  static properties = {
    noResults: { type: String },
    searchAgain: { type: String },
    searchData: { type: Object },
    searchTerm: { type: String },
    noSearchResult: { type: Boolean },
    suggestedResult: { type: Object },
    openState: { type: String },
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchData = {}
    this.searchTerm = ''
    this.noSearchResult = false
    this.suggestedResult = null

    window.addEventListener('resize', this.handleRenderHeight.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('resize', this.handleRenderHeight.bind(this))
    this.closeButton?.removeEventListener('click', this.closeSearchModal.bind(this))
    this.overlay?.removeEventListener('click', this.closeSearchModal.bind(this))
    this.shadowRoot.removeEventListener('keydown', this.onClickEscape.bind(this))
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.openState === 'closed') return

    this.closeButton = this.shadowRoot.querySelector('.header-search__close')
    this.closeButton?.addEventListener('click', this.closeSearchModal.bind(this))
    this.overlay = this.shadowRoot.querySelector('.header-search__overlay')
    this.overlay?.addEventListener('click', this.closeSearchModal.bind(this))
    this.shadowRoot.addEventListener('keydown', this.onClickEscape.bind(this))

    if (Reflect.ownKeys(this.searchData).length === 0) return
    const { products, collections} =
      this.searchData.resources.results
    this.noSearchResult = [products, collections].every(
      (arr) => arr.length === 0,
    )

    if (this.suggestedResult === null && this.noSearchResult) {
      this.handleResult()
    } else if (this.noSearchResult) {
      this.handleRenderResult(this.suggestedResult)
    } else if (this.searchData.resources.results.collections.length === 0) {
      if (this.suggestedResult) {
        this.handleRenderResult(this.suggestedResult, true)
      } else {
        this.handleResult(true)
      }
    }

    document.documentElement.classList.add('sl-scroll-lock')
    this.handleRenderHeight()
  }

  closeSearchModal() {
    this.searchData = {}
    const searchModalEl = this.getRootNode().host.shadowRoot
    searchModalEl.openState = 'closed'
    document.documentElement.classList.remove('sl-scroll-lock')
    document.querySelector('.header__utility > a:first-child').focus()
  }

  onClickEscape(event) {
    const keyCode = event.keyCode || event.which
    if (keyCode === 27) {
      this.closeSearchModal()
      return
    }
  }

  async handleResult(isOnlyCollection = false) {
    try {
      const response = await fetch(
        `?section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
      }
      const data = await response.text()
      const parser = new DOMParser()
      const htmlDocument = parser.parseFromString(data, 'text/html')
      this.suggestedResult = htmlDocument
      this.handleRenderResult(this.suggestedResult, isOnlyCollection)
    } catch (error) {
      console.error('Error:', error.message)
    }
  }

  handleRenderHeight() {
    if (this.openState === 'closed') return

    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      const resultRec = this.getBoundingClientRect()
      if (window.innerHeight - resultRec.bottom > 0) {
        this.style.maxHeight = 'unset'
      } else {
        this.style.maxHeight = `${window.innerHeight - resultRec.top}px`
      }
    }, 500)
  }

  handleRenderResult(data, isOnlyCollection) {
    const suggestedResult = data?.querySelector('.suggested-results')
    if (!suggestedResult) return

    const listContainer = suggestedResult.querySelector(
      '.header-search__list-container',
    )
    const currentListContainer = this.shadowRoot.querySelector(
      '.header-search__list-container',
    )
    if (listContainer && currentListContainer) {
      currentListContainer.replaceWith(listContainer.cloneNode(true))
    }

    if (!isOnlyCollection) {
      const productContainer = suggestedResult.querySelector(
        '.header-search__product-container',
      )
      const currentProductContainer = this.shadowRoot.querySelector(
        '.header-search__product-container',
      )
      if (productContainer && currentProductContainer) {
        currentProductContainer.replaceWith(productContainer.cloneNode(true))
      }
    }
  }

  render() {
    if (this.openState === 'closed') return nothing
    if (Reflect.ownKeys(this.searchData).length === 0) return nothing
    if (this.noSearchResult) {
      return html`
        <div class="header-search__overlay"></div>
        <div class="header-search__result">
          <sl-icon-button name="x-lg" label="Close" class="header-search__close"></sl-icon-button>
          ${
            this.searchTerm.length > 2
              ? html`<div class="header-search__no-result-content">
                  <h3 class="header-search__no-result-title" tabindex="0">
                    ${this.noResults} "${this.searchTerm}"
                  </h3>
                  <p class="header-search__no-result-description">
                    ${this.searchAgain}
                  </p>
                </div>`
              : nothing
          }
          <div class="header-search__no-result-container">
            <div class="header-search__list-container"></div>
            <div class="header-search__product-container"></div>
          </div>
        </div>
      `
    }
    const displayProducts = this.searchData.resources.results.products.slice(
      0,
      2,
    )

    return html`
        <div class="header-search__overlay"></div>
        <div class="header-search__result">
          <sl-icon-button name="x-lg" label="Close Search Modal" class="header-search__close"></sl-icon-button>
          <div class="header-search__no-result-container">
            <div class="header-search__list-container">
              ${
                this.searchData.resources.results.collections.length > 0
                  ? html` <div class="header-search__collection-list">
                    <h4 class="heading-4">${t('general.search.collections')}</h4>
                    <ul>
                      ${this.searchData.resources.results.collections.map(
                        (collection) => html`
                          <li>
                            <a href=${collection.url}>${collection.title}</a>
                          </li>
                        `,
                      )}
                    </ul>
                  </div>`
                  : nothing
              }
            </div>
            ${
              this.searchData.resources.results.products.length > 0
                ? html` <div class="header-search__product-container">
                  <h4 class="heading-4">${t('general.search.product_results')}</h4>
                  <div class="header-search__product-slide-container">
                    <div class="header-search__product-slide-inner-container">
                      ${displayProducts.map(
                        (product) => html`
                          <div class="header-search__product-card">
                            <nova-product-card-async
                              data-handle=${product.handle}
                            ></nova-product-card-async>
                          </div>
                        `,
                      )}
                    </div>
                    <div class="header-search__actions">
                      <a class="btn" variant="secondary-reverse" href="/search?q=${this.searchTerm}">${t('general.search.view_all_product')}</a>
                    </div>
                  </div>
                </div>`
                : nothing
            }
          </div>
        </div>
      `
  }

  static styles = css`
    :host .header-search__overlay {
      position: fixed;
      left: 0;
      height: calc(100dvh - var(--header-primary-height));
      right: 0;
      top: var(--header-primary-height);
      background: rgba(0, 0, 0, 0.3);
      cursor: pointer;
      z-index: -1;
    }

    :host .header-search__result {
      display: flex;
      width: 100%;
      overflow: hidden;
      flex-direction: column;
      max-height: inherit;
      overflow-y: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      position: relative;
    }

    :host .header-search__result::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__close {
      position: absolute;
      top: var(--nova-space-md);
      right: var(--nova-space-md);
      font-size: 18px;
    }

    @media screen and (max-width: 1023px) {
      :host .header-search__close {
        position: fixed;
        right: calc(var(--nova-space-page-margin) + var(--nova-space-sm));
        top: calc(var(--header-primary-height) + 20px);
        background: var(--nova-color-grey-100);
      }
    }

    :host .header-search__close::part(base) {
      padding: 0;
    }

    :host .header-search__product-container {
      display: flex;
      flex-direction: column;
      padding-block: var(--nova-space-md);
      width: 100%;
      gap: var(--nova-space-sm);
    }

    :host .header-search__list-container {
      padding-block: var(--nova-space-md);
      width: 100%;
    }

    :host .header-search__collection-list {
      display: flex;
      flex-direction: column;
      width: 100%;
      gap: var(--nova-space-md);
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__result {
        position: fixed;
        left: 0;
        top: var(--header-primary-height);
        background: var(--nova-color-grey-100);
        z-index: 1;
      }

      :host .header-search__list-container {
        padding: var(--nova-space-none);
        width: auto;
      }

      :host .header-search__product-container {
        padding: var(--nova-space-none);
        width: 67%;
        max-width: 880px;
      }
    }

    :host .header-search__product-slide-container {
      overflow-x: auto;
      width: 100%;
      max-width: 100%;
      -ms-overflow-style: none; /* Hide scrollbar for IE and Edge */
      scrollbar-width: none; /* Hide scrollbar for Firefox */
    }

    /* Hide scrollbar for Chrome, Safari and Opera */
    :host .header-search__product-slide-container::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__product-slide-inner-container {
      display: grid;
      gap: var(--nova-space-sm);
    }

    @media screen and (min-width: 768px) {
      :host .header-search__product-slide-inner-container {
        grid-template-columns: repeat(2, 1fr);
        gap: var(--nova-space-sm);
      }
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__product-slide-inner-container {
        gap: var(--nova-space-md);
      }

      :host .header-search__product-slide-container {
        overflow: visible;
      }
    }

    :host h3 {
      font: var(--nova-h4-font);
      margin: 0;
      color: var(--nova-color-grey-600);
    }

    :host .header-search__list-container ul {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-md);
      padding-left: var(--nova-space-none);
      margin: var(--nova-space-none);
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__list-container ul {
        gap: 15px;
      }
    }

    :host .header-search__list-container li {
      list-style: none;
      color: var(--nova-color-subdued);
    }

    :host .header-search__list-container a {
      font: var(--nova-body-utility);
      letter-spacing: var(--nova-utility-letter-spacing);
	    text-transform: var(--nova-utility-text-transform);
      color: var(--nova-color-black);
      padding-bottom: var(--nova-space-2xs);
      text-decoration: none;
      position: relative;
    }

    :host .header-search__list-container a::after {
      content: '';
      position: absolute;
      height: 1px !important;
      overflow: hidden;
      background-color: transparent;
      width: 0;
      bottom: 0;
      left: 0;
      transition: all var(--nova-button-animation-duration) var(--nova-animation-easing);
    }

    :host .header-search__list-container a:hover::after {
      background-color: var(--nova-color-grey-600);
      width: 100%;
      height: 2px !important;
    }

    :host .header-search__no-result-content {
      padding-block: var(--nova-space-md);
      text-align: center;
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__no-result-content {
        padding: var(--nova-space-md) var(--nova-space-page-margin);
      }
    }

    :host .header-search__no-result-title {
      font: var(--nova-h3-font);
      margin: 0 0 var(--nova-space-xs);
      color: var(--nova-color-grey-500);
    }

    :host .header-search__no-result-description {
      color: var(--nova-color-grey-400);
      margin: 0;
    }

    :host .header-search__no-result-container {
      display: flex;
      flex-direction: column;
    }

    @media screen and (min-width: 1024px) {
      :host .header-search__no-result-container {
        flex-direction: row;
        justify-content: space-between;
        gap: var(--nova-space-lg);
        padding: var(--nova-space-md) var(--nova-space-page-margin);
      }
    }

    .header-search__suggested-card {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-xs);
    }

    .header-search__suggested-card__image {
      aspect-ratio: var(--aspect-ratio-mobile);
      overflow: hidden;
    }

    @media screen and (min-width: 768px) {
      .header-search__suggested-card__image {
        aspect-ratio: var(--aspect-ratio);
      }
    }

    .header-search__suggested-card__image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .header-search__suggested-card__title {
      font: var(--nova-h5-font);
      letter-spacing: var(--nova-h5-letter-spacing);
      text-transform: var(--nova-h5-letter-transform);
      color: var(--nova-color-grey-600);
      text-decoration: none;
    }

    :host .js-product-card-v2 {
      --aspect-ratio-mobile: 397 / 292;
      --aspect-ratio: 397 / 292;
    }

    :host .pcard-v2 {
      display: flex;
      flex-direction: column;
      position: relative;
      container-type: inline-size;
      width: 100%;
      height: 100%;
      padding: 15px var(--nova-space-xs);
      background: var(--nova-color-white);
      box-sizing: border-box;
    }

    :host .pcard-v2 .hidden {
      display: none !important;
    }

    :host .pcard-v2__content {
      display: flex;
      flex-direction: column;
      gap: var(--nova-space-sm);
    }

    :host .pcard-v2__header {
      display: flex;
      align-items: start;
      gap: var(--nova-space-2xs);
      flex-direction: column;
    }

    :host .pcard-v2__media {
      position: relative;
      margin-bottom: var(--nova-space-md);
    }

    :host .pcard-v2 .badges {
      position: absolute;
      top: calc(0px - var(--nova-space-2xs));
      left: 0;
      display: flex;
      gap: var(--nova-space-2xs);
    }

    :host .pcard-v2__image {
      width: 100%;
      aspect-ratio: var(--aspect-ratio-mobile, 155 / 95);
      transition: opacity 0.3s ease-in-out;
    }

    :host .pcard-v2__image svg,
    :host .pcard-v2__image img {
      width: 100%;
      height: 100%;
    }

    :host .pcard-v2__image a {
      display: block;
      height: 100%;
    }

    :host .pcard-v2__image img {
      display: block;
      object-fit: contain;
      object-position: center;
    }

    :host .pcard-v2__image--primary {
      opacity: 1;
    }

    :host .pcard-v2__image--secondary {
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
    }

    :host .pcard-v2__title {
      flex: 1;
      margin: 0;
      font: var(--nova-body-font);
      letter-spacing: var(--nova-body-letter-spacing);
      text-transform: var(--nova-body-text-transform);
      color: var(--nova-color-grey-600);
    }

    :host .pcard-v2__title a {
      color: inherit;
      text-decoration: none;
    }

    :host .pcard-v2__price {
      font: var(--nova-body-font);
      letter-spacing: var(--nova-body-letter-spacing);
      text-transform: var(--nova-body-text-transform);
      color: var(--nova-color-grey-600);
    }

    :host .pcard-v2__price dd,
    :host .pcard-v2__price dl {
      margin: 0;
    }

    :host .pcard-v2__price dl {
      display: flex;
      flex-direction: column;
    }

    :host .pcard-v2__info {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    :host .pcard-v2__swatch-container {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      width: fit-content;
      gap: var(--nova-space-xs);
    }

    :host .pcard-v2__swatch-wrapper,
    :host .pcard-v2__swatch-skeleton {
      display: flex;
      gap: var(--nova-space-2xs);
    }

    :host .pcard-v2__swatch-skeleton span {
      width: var(--nova-product-color-swatch-size-active);
      height: var(--nova-product-color-swatch-size-active);
      border-radius: 50%;
      background: linear-gradient(90deg, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0.06));
      background-size: 200% 100%;
      animation: pcard-v2-skeleton-shimmer 1.2s linear infinite;
    }

    :host .pcard-v2__swatch {
      width: var(--nova-product-color-swatch-size-active);
      height: var(--nova-product-color-swatch-size-active);
      max-width: var(--nova-product-color-swatch-size-active);
      border: 1px solid transparent;
      border-radius: 50%;
      text-indent: -9999px;
      position: relative;
      padding: 0;
      background: transparent;
      cursor: pointer;
    }

    :host .pcard-v2__swatch--active {
      border: 1px solid var(--nova-color-grey-600);
    }

    :host .pcard-v2__swatch span {
      position: absolute;
      text-indent: 50px;
      overflow: hidden;
      border-radius: 50%;
      display: block;
      background-size: cover;
      width: var(--nova-product-color-swatch-size);
      height: var(--nova-product-color-swatch-size);
      top: 50%;
      left: 50%;
      transform: translate(calc(0px - var(--nova-product-color-swatch-size) / 2), calc(0px - var(--nova-product-color-swatch-size) / 2));
      pointer-events: none;
      color: transparent;
    }

    :host .pcard-v2__swatch--sold-out::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, transparent calc(50% - 1px), var(--nova-color-foreground) 50%, transparent calc(50% + 1px));
      pointer-events: none;
    }

    :host .pcard-v2__swatch:nth-child(n + 4) {
      display: none;
    }

    :host .pcard-v2__swatch-container__trigger {
      font: var(--nova-body-utility);
      letter-spacing: var(--nova-utility-letter-spacing);
      text-transform: var(--nova-utility-text-transform);
      color: var(--nova-color-grey-400);
    }

    :host .pcard-v2__swatch-container__trigger a {
      color: inherit;
      text-decoration: none;
      cursor: pointer;
    }

    :host .pcard-v2 .price {
      align-items: center;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      color: var(--nova-color-grey-600);
    }

    :host .pcard-v2 .price__sale,
    :host .pcard-v2 .price--on-sale .price__regular {
      display: none;
    }

    :host .pcard-v2 .price--on-sale .price__sale {
      display: flex;
      flex-direction: row;
      margin-right: 5px;
    }

    :host .pcard-v2 .price--on-sale .js-product-card-v2-price-sale-value {
      margin-right: 5px;
    }

    :host .pcard-v2 .price-item--regular {
      text-decoration: none;
    }

    :host .caption {
      font: var(--nova-body-caption);
      letter-spacing: var(--nova-caption-letter-spacing);
      text-transform: var(--nova-caption-text-transform);
    }

    :host .flex {
      display: flex;
    }

    :host .visually-hidden,
    :host .visually-hidden--inline {
      border: 0 !important;
      clip: rect(0 0 0 0) !important;
      height: 1px !important;
      margin: -1px !important;
      overflow: hidden !important;
      padding: 0 !important;
      position: absolute !important;
      width: 1px !important;
      white-space: nowrap !important;
    }

    :host .pcard-v2 .price--on-sale .price__compare .price-item--regular {
      text-decoration: line-through;
      color: var(--nova-color-grey-400, rgba(0, 0, 0, 0.8));
    }

    :host .pcard-v2 .badges__item {
      align-items: flex-start;
      border-radius: var(--nova-border-rounded, 4px);
      background: var(--badge-bg-color, --nova-color-grey-100);
      color: var(--badge-color, --nova-color-grey-600);
      display: flex;
      padding: var(--nova-space-2xs, 5px) 15px;
      pointer-events: none;
      font: var(--nova-body-caption);
      letter-spacing: var(--nova-caption-letter-spacing);
      text-transform: var(--nova-caption-text-transform);
    }

    :host .pcard-v2__wishlist {
      position: absolute;
      right: var(--nova-space-xs);
      top: var(--nova-space-sm);
      opacity: 0;
      transition: opacity 0.2s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.75rem;
      height: 2.75rem;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;
    }

    :host .pcard-v2__wishlist svg {
      pointer-events: none;
    }

    @keyframes pcard-v2-skeleton-shimmer {
      0% {
        background-position: 200% 0;
      }

      100% {
        background-position: -200% 0;
      }
    }

    .header-search__actions {
      display: flex;
      justify-content: flex-end;
      padding-top: var(--nova-space-md);
    }

    .btn {
      border-width: 1px;
      height: var(--nova-button-height);
      display: inline-flex;
      gap: var(--nova-button-gap);
      flex-shrink: 0;
      justify-content: center;
      align-items: center;
      padding-inline: var(--nova-button-padding-left-right);
      font: var(--nova-body-utility);
      letter-spacing: var(--nova-utility-letter-spacing);
      text-transform: var(--nova-utility-text-transform);
      transition: all var(--nova-button-animation-duration) var(--nova-animation-easing);
      text-decoration: none;
    }

    .btn[variant="secondary-reverse"] {
      border: 0;
      color: var(--nova-color-background);
      background-color: var(--nova-color-grey-600);
    }

    .btn[variant="secondary-reverse"]:not(:disabled):hover {
      color: var(--nova-color-foreground);
      background-color: var(--nova-color-grey-100);
    }

    .btn[variant="secondary-reverse"]:disabled {
      color: var(--nova-color-background);
      background-color: var(--nova-color-grey-300);
      cursor: default;
    }

    .btn[variant="secondary-reverse"]:focus,
    .btn[variant="secondary-reverse"]:focus-visible {
      color: var(--nova-color-background);
      background-color: var(--nova-color-grey-600);
      outline: var(--nova-color-focus) solid 2px;
    }

    @media screen and (min-width: 768px) {
      :host .pcard-v2__content {
        flex-direction: row-reverse;
        justify-content: space-between;
        gap: var(--nova-space-xs);
      }

      :host .pcard-v2__media {
        margin-bottom: 15px;
      }

      :host .pcard-v2__image {
        aspect-ratio: var(--aspect-ratio, 1);
      }

      :host .pcard-v2__swatch--sold-out::after {
        top: 1px;
        left: 1px;
        right: 1px;
        bottom: 1px;
      }
    }

    @container (min-width: 300px) {
      :host .pcard-v2--enable-hover:hover .pcard-v2__image--primary {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
      }

      :host .pcard-v2--enable-hover:hover .pcard-v2__image--secondary {
        opacity: 1;
        position: relative;
      }

      :host .pcard-v2__media:hover .pcard-v2__wishlist {
        opacity: 1;
      }
    }
  `
}

customElements.define('nova-search-result', NovaSearchResult)
